/**
 * Logout Service
 *
 * Handles logout orchestration with fail-open semantics.
 *
 * Enterprise logout pattern: no SPA navigation before Azure redirect.
 * - Set isLoggingOut first so route guards do not navigate to /login.
 * - Then backend logout (best-effort), clear local state, then logoutRedirect().
 * - Flow: Dashboard → Azure logout endpoint → postLogoutRedirectUri (no intermediate Login render).
 *
 * Flow:
 * 0. Set logout-in-progress in AuthContext (prevents ProtectedRoute from redirecting)
 * 1. Invalidate token on backend (best-effort, non-blocking, 5s timeout)
 * 2. Clear all local auth state (safe now; guard will not redirect)
 * 3. Call MSAL logoutRedirect() to end SSO session
 */

import type {
  LogoutParams,
  LogoutResult,
} from '../types/logout.types';
import type {
  MsalAdapter,
  ConfigAdapter,
  StorageAdapter,
  AuthContextAdapter,
} from '../types/adapters.types';
import { tokenService } from '../../../services/token.service';
import { logout as logoutApi } from '../../../services/authApi.service';
import { AUTH_BROADCAST_CHANNEL_NAMES, emitAuthLogoutSignal } from '@hsus-dev/cross-portal-logout';
import { endSession } from '../../../services/session.service';

/**
 * Logout Service
 * 
 * Extracts logout logic from AuthContext.tsx, useMsalAuth.ts, msalAuthProvider.service.ts
 * Uses adapter pattern for dependency injection
 */
export class LogoutService {
  constructor(
    private msalAdapter: MsalAdapter,
    private configAdapter: ConfigAdapter,
    private storageAdapter: StorageAdapter,
    private authContextAdapter: AuthContextAdapter
  ) {}

  /**
   * Execute logout
   * 
   * This method implements fail-open semantics:
   * - Backend logout is best-effort (non-blocking, 5s timeout)
   * - Local state is cleared BEFORE redirect (critical)
   * - MSAL logoutRedirect() is called to end SSO session
   * - Errors are logged but don't block logout
   * 
   * @param params - Optional logout parameters
   * @returns Logout result (always success for fail-open semantics)
   */
  async executeLogout(params?: LogoutParams): Promise<LogoutResult> {
    let backendLogoutError: Error | undefined;

    // Step 0: Set logout-in-progress so route guards do NOT navigate to /login before Azure redirect.
    this.authContextAdapter.setLogoutInProgress(true);

    try {
      // Step 1: Backend logout (best-effort, non-blocking, 5s timeout)
      if (params?.accessToken) {
        try {
          // Use Promise.race to enforce 5s timeout
          await Promise.race([
            logoutApi(params.accessToken),
            new Promise<void>((_, reject) =>
              setTimeout(() => reject(new Error('Backend logout timeout')), 5000)
            ),
          ]);
        } catch (error) {
          // Log but don't block - fail-open semantics
          backendLogoutError = error as Error;
          console.error('[LogoutService] Backend logout failed (non-blocking):', error);
        }
      } else {
        // Try to get token from service for backend logout
        try {
          const currentToken = await tokenService.getAccessToken();
          if (currentToken) {
            try {
              await Promise.race([
                logoutApi(currentToken),
                new Promise<void>((_, reject) =>
                  setTimeout(() => reject(new Error('Backend logout timeout')), 5000)
                ),
              ]);
            } catch (error) {
              backendLogoutError = error as Error;
              console.error('[LogoutService] Backend logout failed (non-blocking):', error);
            }
          }
        } catch (error) {
          console.warn('[LogoutService] Could not retrieve token for backend logout:', error);
        }
      }
    } catch (error) {
      // Log but don't block - fail-open semantics
      backendLogoutError = error as Error;
      console.error('[LogoutService] Backend logout error (non-blocking):', error);
    }

    // Step 2: Clear all local auth state BEFORE redirect (CRITICAL)
    // Code after logoutRedirect() won't execute, so we must clear state first.
    // Session policy: tear down timers before localStorage.clear so the singleton is not left
    // Active with no hsus_* keys (would block absolute/idle timers on next login in-SPA).
    try {
      endSession('user', { invokeLogoutCallback: false });
    } catch (e) {
      console.error('[LogoutService] endSession (non-blocking):', e);
    }

    // Get active account BEFORE clearing storage so clearMsalCache can clear in-memory state
    // (after clearAllStorage(), getActiveAccount() may return null so we'd skip setActiveAccount(null)).
    const activeAccount = this.msalAdapter.getActiveAccount();

    // Notify other tabs to log out (cross-tab logout) before clearing storage
    try {
      emitAuthLogoutSignal(AUTH_BROADCAST_CHANNEL_NAMES.HEALTHCARE);
    } catch (e) {
      // Non-blocking; other tabs may still see storage event
    }

    try {
      await this.storageAdapter.clearAllStorage();
    } catch (error) {
      console.error('[LogoutService] Error clearing session/local storage (non-blocking):', error);
    }

    try {
      // Clear auth context state
      await this.authContextAdapter.clearAuthStateSilently();

      // Clear tokens
      await this.storageAdapter.clearTokens();

      // Clear secure storage
      await this.storageAdapter.clearSecureStorage();

      // Clear MSAL in-memory active account and any remaining MSAL cache keys (both storages)
      this.storageAdapter.clearMsalCache(activeAccount);

      // Also call MSAL's clearCache() API to ensure in-memory state is cleared
      try {
        await this.msalAdapter.clearCache();
      } catch (error) {
        console.warn('[LogoutService] Error calling MSAL clearCache (non-blocking):', error);
      }

      // Clear persisted MSAL account ID (in case MSAL wrote it after clearAllStorage)
      try {
        localStorage.removeItem('msal_last_account_id');
      } catch (error) {
        console.warn('[LogoutService] Error removing msal_last_account_id:', error);
      }
    } catch (error) {
      // Log but continue - fail-open semantics; session/local already cleared above
      console.error('[LogoutService] Error clearing local state (non-blocking):', error);
    }

    // Step 3: Call MSAL logoutRedirect() to end SSO session
    // CRITICAL: Pass the activeAccount so Azure clears the server-side session for this user.
    // Without account, Azure keeps its session cookie and forwards login_hint/username to Google.
    // This will navigate away, so code after this won't execute.
    try {
      const postLogoutUri = this.configAdapter.getPostLogoutRedirectUri();
      await this.msalAdapter.logoutRedirect(postLogoutUri, activeAccount);
    } catch (error) {
      // If MSAL logout fails, fallback to direct Azure logout URL
      console.error('[LogoutService] MSAL logoutRedirect failed, using fallback:', error);
      
      const authority = import.meta.env.VITE_AZURE_AUTHORITY ?? 'https://login.microsoftonline.com/common';
      const postLogoutUri = this.configAdapter.getPostLogoutRedirectUri();
      const logoutUrl = `${authority}/oauth2/v2.0/logout?post_logout_redirect_uri=${encodeURIComponent(postLogoutUri)}`;
      window.location.href = logoutUrl;
    }

    // Return success (fail-open semantics - always complete from user perspective)
    return {
      success: true,
      error: backendLogoutError, // Include error for logging, but don't fail
    };
  }
}
